import { useEffect, useState, ChangeEvent } from 'react';
import { NavLink } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import {
  IconUp,
  IconDown,
  OnSelectChange,
  InputText,
  Select,
  PermissionEnum,
  IconTrash,
  PaymentType,
  usePrevious,
  ButtonPseudo,
  IconExpand,
  ButtonSize,
  SelectItemProps,
  IconChangeLoClock,
} from '@ui/src';
import {
  ExternalAccountTopUpOptionItemRowTypes,
  ExternalAccountTopUpOptionTypes,
} from './types';
import { ExternalAccountTopUpOptionFormValues } from '../../hoc/external/types';
import RoutingCode from './routing-codes';

import cn from './styles.module.css';

const ExternalAccountTopUpOptionItemRow = ({
  title,
  value,
  isEditable,
  type,
  name,
  inputValue,
  placeholder,
  onChange,
  list,
  disabled,
  error,
}: ExternalAccountTopUpOptionItemRowTypes) => {
  let content;

  if (!isEditable) {
    content = (
      <div className={cn.externalAccountTopUpOptionRow}>
        <div className={cn.externalAccountTopUpOptionRowTitle}>{title}</div>
        <div className={cn.externalAccountTopUpOptionRowValue}>{value}</div>
      </div>
    );
  } else if (isEditable) {
    let inputContent;

    if (type === 'input') {
      inputContent = (
        <InputText
          name={name}
          value={inputValue}
          title={title}
          placeholder={placeholder}
          onChange={(e) =>
            onChange({
              target: {
                name,
                value: e.target.value,
              },
            } as ChangeEvent<HTMLInputElement>)
          }
          error={error}
        />
      );
    } else if (type === 'select') {
      inputContent = (
        <Select
          name={name}
          value={inputValue}
          title={title}
          list={list || []}
          placeholder={placeholder}
          onChange={(e) =>
            onChange({
              target: {
                name,
                value: e.target.value as string,
              },
            } as ChangeEvent<HTMLInputElement>)
          }
          disabled={disabled}
          error={error}
          fullWidth
          withClearButton
        />
      );
    }

    content = (
      <div className={cn.externalAccountTopUpOptionFormGroup}>
        {inputContent}
      </div>
    );
  }

  return content;
};

const ExternalAccountTopUpOptionItem = ({
  topUpOption,
  paymentTypes,
  currencies,
  isEditable,
  newOption,
  activePermissions,
  setSelectedOptionId,
  setDeleteConfirmationVisible,
  setTopUpOptions,
  topUpOptions,
  optionsError,
  optionsErrors,
  setOptionsErrors,
}: ExternalAccountTopUpOptionTypes) => {
  const [collapsed, setCollapsed] = useState(true);
  const [formValues, setFormValues] =
    useState<ExternalAccountTopUpOptionFormValues>();
  const [availableCurrencies, setAvailableCurrencies] = useState<
    SelectItemProps[]
  >([]);

  const prevPaymentType = usePrevious(formValues?.paymentType);

  const onChange = (e: ChangeEvent<HTMLInputElement> | OnSelectChange) => {
    const nextFormValues = { ...formValues };

    const { name, value } = e.target;

    const nextOptions = topUpOptions?.map((item) => {
      const result = { ...item };

      if (topUpOption?.id === result?.id) {
        if (name === `paymentType-${topUpOption?.id}`) {
          nextFormValues.paymentType = value as string;
          nextFormValues.currencyId = '';
          result.paymentType = value as string;
          result.currencyId = '';

          if (optionsError?.currencyId || optionsError?.paymentType) {
            const nextOptionsErrors = optionsErrors?.map((error) => {
              const errorResult = { ...error };

              if (errorResult?.id === optionsError?.id) {
                errorResult.currencyId = '';
                errorResult.paymentType = '';
              }

              return errorResult;
            });

            setOptionsErrors(
              nextOptionsErrors as {
                id: string;
                currencyId?: string;
                paymentType?: string;
              }[],
            );
          }
        } else if (name === `currencyId-${topUpOption?.id}`) {
          nextFormValues.currencyId = value as string;
          result.currencyId = value as string;

          if (optionsError?.currencyId) {
            const nextOptionsErrors = optionsErrors?.map((error) => {
              const errorResult = { ...error };

              if (errorResult?.id === optionsError?.id) {
                errorResult.currencyId = '';
              }

              return errorResult;
            });

            setOptionsErrors(
              nextOptionsErrors as {
                id: string;
                currencyId?: string;
                paymentType?: string;
              }[],
            );
          }
        } else if (name === `bankAccountHolderName-${topUpOption?.id}`) {
          nextFormValues.bankAccountHolderName = value as string;
          result.bankAccountHolderName = value as string;
        } else if (name === `accountNumber-${topUpOption?.id}`) {
          nextFormValues.accountNumber = value as string;
          result.accountNumber = value as string;
        } else if (name === `bankAddress-${topUpOption?.id}`) {
          nextFormValues.bankAddress = value as string;
          result.bankAddress = value as string;
        } else if (name === `bic-${topUpOption?.id}`) {
          nextFormValues.bic = value as string;
          result.bic = value as string;
        } else if (name === `iban-${topUpOption?.id}`) {
          nextFormValues.iban = value as string;
          result.iban = value as string;
        } else if (name === `bankName-${topUpOption?.id}`) {
          nextFormValues.bankName = value as string;
          result.bankName = value as string;
        } else if (name === `thirdPartyReference-${topUpOption?.id}`) {
          nextFormValues.thirdPartyReference = value as string;
          result.thirdPartyReference = value as string;
        }
      }

      return result;
    });

    setFormValues(nextFormValues as ExternalAccountTopUpOptionFormValues);

    if (setTopUpOptions) {
      setTopUpOptions(nextOptions as ExternalAccountTopUpOptionFormValues[]);
    }
  };

  useEffect(() => {
    if (topUpOption) {
      setFormValues(topUpOption);
    }
  }, [topUpOption]);

  useEffect(() => {
    if (newOption) {
      setCollapsed(false);
    }
  }, [newOption]);

  useEffect(() => {
    if (
      topUpOption?.id?.includes('option') &&
      ((formValues?.paymentType === PaymentType.FEDWIRE &&
        prevPaymentType !== PaymentType.FEDWIRE) ||
        (formValues?.paymentType === PaymentType.BACS &&
          prevPaymentType !== PaymentType.BACS) ||
        (formValues?.paymentType === PaymentType.SWIFT &&
          prevPaymentType !== PaymentType.SWIFT) ||
        (formValues?.paymentType === PaymentType.FASTER_PAYMENT &&
          prevPaymentType !== PaymentType.FASTER_PAYMENT))
    ) {
      setFormValues({
        ...formValues,
        routingCodeEntries: [
          {
            id: `routingCode-${uuid()}`,
            routingCodeKey: '',
            routingCodeValue: '',
          },
        ],
      });

      const nextOptions = topUpOptions?.map((item) => {
        const result = { ...item };

        if (result.id === topUpOption?.id) {
          result.routingCodeEntries = [
            {
              id: `routingCode-${uuid()}`,
              routingCodeKey: '',
              routingCodeValue: '',
            },
          ];
        }

        return result;
      });

      if (setTopUpOptions) {
        setTopUpOptions(nextOptions as ExternalAccountTopUpOptionFormValues[]);
      }
    } else if (
      prevPaymentType &&
      (prevPaymentType === PaymentType.FEDWIRE ||
        prevPaymentType === PaymentType.BACS ||
        prevPaymentType === PaymentType.SWIFT ||
        prevPaymentType === PaymentType.FASTER_PAYMENT) &&
      prevPaymentType !== formValues?.paymentType
    ) {
      setFormValues({
        ...formValues,
        routingCodeEntries: [
          {
            id: `routingCode-${uuid()}`,
            routingCodeKey: '',
            routingCodeValue: '',
          },
        ],
      } as ExternalAccountTopUpOptionFormValues);

      const nextOptions = topUpOptions?.map((item) => {
        const result = { ...item };

        if (result.id === topUpOption?.id) {
          result.routingCodeEntries = [
            {
              id: `routingCode-${uuid()}`,
              routingCodeKey: '',
              routingCodeValue: '',
            },
          ];
        }

        return result;
      });

      if (setTopUpOptions) {
        setTopUpOptions(nextOptions as ExternalAccountTopUpOptionFormValues[]);
      }
    }
  }, [formValues, prevPaymentType]);

  useEffect(() => {
    if (formValues?.paymentType) {
      const currentType = paymentTypes?.find(
        (item) => item.paymentType === formValues?.paymentType,
      );

      if (currentType) {
        setAvailableCurrencies(
          currentType?.currencies?.edges?.map((item) => ({
            name: item.node.description,
            value: item.node.id,
            suffix: item.node.name,
          })),
        );
      }
    }
  }, [formValues?.paymentType]);

  useEffect(() => {
    if (optionsError && collapsed) {
      setCollapsed(false);
    }
  }, [optionsError]);

  let routingCodeTitle = 'Routing code';

  if (formValues?.paymentType === PaymentType.FEDWIRE) {
    routingCodeTitle = 'ABA routing number';
  } else if (
    formValues?.paymentType === PaymentType.BACS ||
    formValues?.paymentType === PaymentType.FASTER_PAYMENT
  ) {
    routingCodeTitle = 'Sort code';
  }

  let title;

  if (newOption) {
    title = 'New Option';
  } else {
    title = topUpOption?.paymentType
      ? paymentTypes?.find(
          (item) => item.paymentType === topUpOption.paymentType,
        )?.name
      : 'Available Option';
  }

  return (
    <div className={cn.externalAccountTopUpOption}>
      <div className={cn.externalAccountTopUpOptionButton}>
        <button
          className={cn.externalAccountTopUpOptionButtonLeft}
          type="button"
          onClick={() => setCollapsed(!collapsed)}
        >
          <span>{title}</span>
          {topUpOption.currencyId && (
            <div className={cn.externalAccountTopUpOptionButtonCurrency}>
              {
                currencies?.find(
                  (item) => item.value === topUpOption?.currencyId,
                )?.suffix
              }
            </div>
          )}
        </button>
        <div className={cn.externalAccountTopUpOptionButtonRight}>
          {isEditable &&
            !topUpOption?.id?.includes('option') &&
            activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <div className={cn.optionChangeLogs}>
                <NavLink
                  to={`/administration/change-logs?entityType=PSP_INTEGRATION_PAYMENT_ACCOUNT&entityId=${topUpOption?.id}`}
                  target="_blank"
                  className={cn.changeLogLink}
                >
                  <IconChangeLoClock />
                  Change Log
                </NavLink>
              </div>
            )}
          {isEditable &&
            activePermissions?.includes(
              PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_DELETE_ACTION,
            ) && (
              <button
                type="button"
                className={cn.deleteOptionButton}
                onClick={() => {
                  if (topUpOption?.id?.includes('option') && setTopUpOptions) {
                    setTopUpOptions(
                      topUpOptions?.filter(
                        (item) => item.id !== topUpOption?.id,
                      ) as ExternalAccountTopUpOptionFormValues[],
                    );

                    if (
                      optionsErrors?.find((item) => item.id === topUpOption?.id)
                    ) {
                      setOptionsErrors(
                        optionsErrors?.filter(
                          (item) => item.id !== topUpOption?.id,
                        ),
                      );
                    }
                  } else {
                    if (setSelectedOptionId) {
                      setSelectedOptionId(topUpOption?.id as string);
                    }
                    if (setDeleteConfirmationVisible) {
                      setDeleteConfirmationVisible(true);
                    }
                  }
                }}
              >
                <IconTrash />
              </button>
            )}
          <button
            className={cn.externalAccountTopUpOptionButtonIcon}
            type="button"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? (
              <IconDown width={20} height={20} />
            ) : (
              <IconUp width={20} height={20} />
            )}
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className={cn.externalAccountTopUpOptionContent}>
          {isEditable && (
            <ExternalAccountTopUpOptionItemRow
              title="Payment Type"
              value={topUpOption.paymentType}
              type="select"
              isEditable={isEditable}
              name={`paymentType-${topUpOption?.id}`}
              inputValue={formValues?.paymentType as string}
              placeholder="Select Payment type"
              onChange={onChange}
              list={paymentTypes?.map((item) => ({
                name: item.name,
                value: item.paymentType,
              }))}
              error={optionsError?.paymentType}
            />
          )}
          <ExternalAccountTopUpOptionItemRow
            title="Currency"
            value={
              topUpOption.currencyId
                ? (currencies?.find(
                    (item) => item.value === topUpOption?.currencyId,
                  )?.suffix as string)
                : '-'
            }
            type="select"
            isEditable={isEditable as boolean}
            name={`currencyId-${topUpOption?.id}`}
            inputValue={formValues?.currencyId as string}
            placeholder="Select Currency"
            onChange={onChange}
            list={isEditable ? availableCurrencies : currencies}
            disabled={!formValues?.paymentType}
            error={optionsError?.currencyId}
          />
          <ExternalAccountTopUpOptionItemRow
            title="Beneficiary"
            value={topUpOption.bankAccountHolderName}
            isEditable={isEditable as boolean}
            type="input"
            name={`bankAccountHolderName-${topUpOption?.id}`}
            inputValue={formValues?.bankAccountHolderName as string}
            placeholder="Enter Beneficiary name"
            onChange={onChange}
          />
          <ExternalAccountTopUpOptionItemRow
            title="Beneficiary account"
            value={topUpOption.accountNumber}
            isEditable={isEditable as boolean}
            type="input"
            name={`accountNumber-${topUpOption?.id}`}
            inputValue={formValues?.accountNumber as string}
            placeholder="Enter Beneficiary account number"
            onChange={onChange}
          />
          {!isEditable &&
            formValues?.routingCodeEntries &&
            formValues?.routingCodeEntries?.length > 0 && (
              <ExternalAccountTopUpOptionItemRow
                title="Routing Codes"
                value={formValues?.routingCodeEntries
                  ?.map(
                    (item) =>
                      `${item?.routingCodeKey?.split('_')?.join(' ')}: ${item?.routingCodeValue}`,
                  )
                  ?.join(', ')}
                isEditable={isEditable as boolean}
                type="input"
                name={`routingCodeEntries-${topUpOption?.id}`}
                inputValue=""
                placeholder="Enter Beneficiary account number"
                onChange={onChange}
              />
            )}
          {isEditable &&
            (formValues?.paymentType === PaymentType.FASTER_PAYMENT ||
              formValues?.paymentType === PaymentType.FEDWIRE ||
              formValues?.paymentType === PaymentType.SWIFT ||
              formValues?.paymentType === PaymentType.BACS) && (
              <div className={cn.routingCodesWrapper}>
                {formValues?.routingCodeEntries?.map((item, index) => (
                  <RoutingCode
                    key={item.id}
                    routingCodeValue={item}
                    paymentType={formValues?.paymentType}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    setTopUpOptions={setTopUpOptions}
                    topUpOptions={topUpOptions}
                    topUpId={topUpOption?.id as string}
                    index={index}
                  />
                ))}
                <ButtonPseudo
                  icon={<IconExpand />}
                  text={`Add ${routingCodeTitle}`}
                  size={ButtonSize.SMALL}
                  className={`button-white ${cn.addRoutingCodeButton}`}
                  onClick={() => {
                    const nextFormValues = { ...formValues };
                    const nextRoutingCodes = (
                      [] as {
                        id?: string;
                        routingCodeKey: string;
                        routingCodeValue: string;
                      }[]
                    ).concat(
                      nextFormValues.routingCodeEntries as {
                        id?: string;
                        routingCodeKey: string;
                        routingCodeValue: string;
                      }[],
                    );

                    nextRoutingCodes.push({
                      id: `routingCode-${uuid()}`,
                      routingCodeKey: '',
                      routingCodeValue: '',
                    });

                    nextFormValues.routingCodeEntries = nextRoutingCodes;
                    setFormValues(nextFormValues);
                  }}
                />
              </div>
            )}
          <ExternalAccountTopUpOptionItemRow
            title="Beneficiary bank address:"
            value={topUpOption.bankAddress}
            isEditable={isEditable as boolean}
            type="input"
            name={`bankAddress-${topUpOption?.id}`}
            inputValue={formValues?.bankAddress as string}
            placeholder="Enter Beneficiary bank address"
            onChange={onChange}
          />
          <ExternalAccountTopUpOptionItemRow
            title="Beneficiary Bank"
            value={topUpOption.bankName}
            isEditable={isEditable as boolean}
            type="input"
            name={`bankName-${topUpOption?.id}`}
            inputValue={formValues?.bankName as string}
            placeholder="Enter Beneficiary bank name"
            onChange={onChange}
          />
          <ExternalAccountTopUpOptionItemRow
            title="Beneficiary Bank BIC"
            value={topUpOption.bic || '-'}
            type="input"
            isEditable={isEditable as boolean}
            name={`bic-${topUpOption?.id}`}
            inputValue={formValues?.bic as string}
            placeholder="Enter Beneficiary bank BIC"
            onChange={onChange}
          />
          <ExternalAccountTopUpOptionItemRow
            title="Beneficiary Bank IBAN"
            value={topUpOption.iban || '-'}
            type="input"
            isEditable={isEditable as boolean}
            name={`iban-${topUpOption?.id}`}
            inputValue={formValues?.iban as string}
            placeholder="Enter Beneficiary bank IBAN"
            onChange={onChange}
          />

          <ExternalAccountTopUpOptionItemRow
            title="Purpose of the payment"
            value={topUpOption?.thirdPartyReference || '-'}
            type="input"
            isEditable={isEditable as boolean}
            name={`thirdPartyReference-${topUpOption?.id}`}
            inputValue={formValues?.thirdPartyReference as string}
            placeholder="Enter payment details"
            onChange={onChange}
          />
        </div>
      )}
    </div>
  );
};

export default ExternalAccountTopUpOptionItem;
